import { Marker } from '@react-google-maps/api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AimoMarkerProps, MarkerTypes } from 'types/MapTypes';
import { markerIconsMap } from 'utils/mapUtils';
import { markerSvg } from './WebMarkerSvgUtil';

const activeSize = { height: 60, width: 60, x: 60 / 2, y: 60 - 4 };
const normalSize = { height: 50, width: 50, x: 50 / 2, y: 50 + 3 };
const zoneCodeSize = { height: 70, width: 70, x: 70 / 2, y: 70 + 3 };

type AimoWebMarkerProps = Omit<AimoMarkerProps, 'markerType'> & {
  iconString: MarkerTypes;
};

const WebMarker = (props: AimoWebMarkerProps) => {
  const { t } = useTranslation();
  const {
    iconString,
    activeIndex,
    onPress,
    coordinate,
    clusterer,
    zoneCode,
    isActive = false,
  } = props;

  const iconUrl = useMemo(() => {
    if (zoneCode) {
      return (
        'data:image/svg+xml;charset=UTF-8,' +
        encodeURIComponent(
          markerSvg(iconString, isActive).replace('zoneCode', zoneCode)
        )
      );
    }
    return `/images/${markerIconsMap[iconString]}.svg`;
  }, [zoneCode, isActive, iconString]);

  const iconSize = useMemo(() => {
    let size;
    if (zoneCode) {
      size = zoneCodeSize;
    } else {
      size = activeIndex ? activeSize : normalSize;
    }
    return new google.maps.Size(size.width, size.height);
  }, [zoneCode, activeIndex]);

  const icon: google.maps.Icon = {
    url: iconUrl,
    size: iconSize,
    scaledSize: iconSize,
    origin: new google.maps.Point(0, activeIndex ? 0 : -4),
  };

  return (
    <Marker
      zIndex={activeIndex}
      clickable={true}
      onClick={onPress}
      icon={icon}
      clusterer={clusterer}
      cursor="pointer"
      position={{ lat: coordinate.latitude, lng: coordinate.longitude }}
      title={zoneCode ? t('parking.zone', { zoneCode }) : t('parking.marker')}
    />
  );
};

export default WebMarker;
